import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  applyStrategiesSnapshot,
  fetchStrategies,
  setStrategiesStreamConnected,
} from "@/state/slices/strategiesSlice";
import type { StrategiesResponse } from "@/api/types";
import { apiUrl } from "@/api/client";

/** Subscribe to `/api/strategies/stream` (SSE) — the shared strategy runner's
 *  live snapshot: every visible strategy's status (so the TabBar pill and the
 *  picker stay current) plus the order book of the SELECTED strategies.
 *
 *  Open to every signed-in user (public, read-only). It connects only once the
 *  list probe reports the feature `enabled`, and reconnects when the selection
 *  changes (the server trims the order book to `ids`). EventSource reconnects
 *  on its own after a drop. */
export function useStrategiesStream(): void {
  const dispatch = useAppDispatch();
  const meStatus = useAppSelector((s) => s.me.status);
  const enabled = useAppSelector((s) => s.strategies.enabled);
  const ids = useAppSelector((s) => s.strategies.selectedIds.join(","));

  useEffect(() => {
    if (meStatus === "succeeded") {
      void dispatch(fetchStrategies());
    }
  }, [dispatch, meStatus]);

  useEffect(() => {
    if (meStatus !== "succeeded" || !enabled) return;
    const qs = ids ? `?ids=${encodeURIComponent(ids)}` : "";
    const source = new EventSource(apiUrl(`/api/strategies/stream${qs}`), {
      withCredentials: true,
    });
    source.addEventListener("open", () => {
      dispatch(setStrategiesStreamConnected(true));
    });
    source.addEventListener("snapshot", (e: MessageEvent) => {
      try {
        const doc: StrategiesResponse = JSON.parse(e.data);
        dispatch(applyStrategiesSnapshot(doc));
      } catch {
        /* ignore a malformed frame; the next snapshot replaces it */
      }
    });
    source.addEventListener("error", () => {
      dispatch(setStrategiesStreamConnected(false));
    });
    return () => {
      source.close();
      dispatch(setStrategiesStreamConnected(false));
    };
  }, [dispatch, meStatus, enabled, ids]);
}
